import { useState } from 'react';
import { searchRegistrationExaminee } from 'src/app/modules/Recruitment/network/license-exam-crud';
import { SearchRegistrationExamineeResponseItem } from 'src/app/modules/Recruitment/types/license-exam-types';

export interface RegistrantFilters {
  firstName: string;
  lastName: string;
  chiName: string;
  identityDocumentNumber: string;
  candidateCode: string;
  agentCode: string;
  recruiterCode: string;
}

interface UseRegistrantSelectionParams {
  examCode: string;
  examType: string;
  resetRegistration?: (registrant: SearchRegistrationExamineeResponseItem | null) => void;
}

const INITIAL_FILTERS: RegistrantFilters = {
  firstName: '',
  lastName: '',
  chiName: '',
  identityDocumentNumber: '',
  candidateCode: '',
  agentCode: '',
  recruiterCode: '',
};

export const useRegistrantSelection = ({ examCode, examType, resetRegistration }: UseRegistrantSelectionParams) => {
  const [filters, setFilters] = useState<RegistrantFilters>(INITIAL_FILTERS);
  const [resultList, setResultList] = useState<SearchRegistrationExamineeResponseItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);

  const updateFilter = <K extends keyof RegistrantFilters>(key: K, value: RegistrantFilters[K]) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSearch = async () => {
    setSearching(true);
    try {
      const trimmedFilters = Object.fromEntries(
        Object.entries(filters).map(([key, value]) => [key, value.trim()]),
      ) as RegistrantFilters;

      const hasTrimmedValue = Object.values(filters).some((value) => value !== value.trim());
      if (hasTrimmedValue) {
        setFilters(trimmedFilters);
      }

      const searchParams = Object.fromEntries(
        Object.entries(trimmedFilters).filter(([, value]) => value.trim() !== ''),
      ) as Partial<RegistrantFilters>;

      if (Object.keys(searchParams).length === 0) {
        setOpenAlert(true);
        return;
      }

      resetRegistration?.(null);

      const response = await searchRegistrationExaminee({
        examCode,
        examType,
        ...searchParams,
      });
      const nextResultList = response;
      setResultList(nextResultList);
    } finally {
      setSearching(false);
    }
  };

  const handleReset = () => setFilters(INITIAL_FILTERS);
  const onAlertClose = () => setOpenAlert(false);

  return {
    filters,
    resultList,
    searching,
    openAlert,
    setOpenAlert,
    updateFilter,
    handleSearch,
    handleReset,
    onAlertClose,
  };
};
